/**
 * Parsing what the SmartLedger Wallet sends back. It redirects to our
 * callback pages with query parameters; anything missing or malformed is
 * treated as an error rather than guessed at.
 */

export type WalletError = { kind: 'cancelled' } | { kind: 'error'; message: string };

export type AuthCallback =
  | { kind: 'ok'; address: string; signature: string; challenge: string }
  | WalletError;

export type AttestCallback =
  | { kind: 'ok'; address: string; signature: string; nonce: string }
  | WalletError;

function walletError(params: URLSearchParams): WalletError | null {
  const error = params.get('error');
  if (!error) return null;
  if (error === 'user_cancelled' || error === 'cancelled' || error === 'access_denied') return { kind: 'cancelled' };
  const description = params.get('error_description');
  return { kind: 'error', message: description || error.replace(/_/g, ' ') };
}

const clean = (v: string | null) => (v ?? '').trim();

export function parseAuthCallback(search: string | URLSearchParams): AuthCallback {
  const params = typeof search === 'string' ? new URLSearchParams(search) : search;
  const err = walletError(params);
  if (err) return err;
  const address = clean(params.get('address'));
  const signature = clean(params.get('signature'));
  const challenge = clean(params.get('challenge'));
  if (!address || !signature || !challenge) {
    return { kind: 'error', message: 'The wallet response was incomplete.' };
  }
  return { kind: 'ok', address, signature, challenge };
}

export function parseAttestCallback(search: string | URLSearchParams): AttestCallback {
  const params = typeof search === 'string' ? new URLSearchParams(search) : search;
  const err = walletError(params);
  if (err) return err;
  const address = clean(params.get('address'));
  const signature = clean(params.get('signature'));
  const nonce = clean(params.get('nonce'));
  if (!address || !signature || !nonce) {
    return { kind: 'error', message: 'The wallet response was incomplete.' };
  }
  return { kind: 'ok', address, signature, nonce };
}

/**
 * Only same-app paths are allowed as a post-sign-in destination, so a crafted
 * link can't bounce the user to another site.
 */
export function safeReturnPath(value: string | null | undefined): string | null {
  if (!value) return null;
  if (!value.startsWith('/') || value.startsWith('//') || value.startsWith('/\\')) return null;
  if (value.startsWith('/auth/')) return null;
  return value;
}
